"use client";

import { useState } from "react";
import { Icon } from "@iconify/react";
import { uiIcons } from "@/config/icons";
import { PDFDocument } from "pdf-lib";
import { FileUploader } from "@/components/ui/FileUploader";
import { usePathname } from "next/navigation";
import { getLocaleFromPathname } from "@/lib/i18n";
import { getPdfToolCopy } from "@/lib/pdf-tool-copy";
import { tools } from "@/config/tools";
import { getLocalizedTool } from "@/lib/tool-localization";

export function JpgToPdf() {
  const [images, setImages] = useState<File[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const pathname = usePathname() || "/";
  const locale = getLocaleFromPathname(pathname);
  const copy = getPdfToolCopy(locale);
  const localizedTool = getLocalizedTool(
    tools.find((item) => item.id === "jpg-to-pdf") ?? tools[0],
    locale
  );

  const handleFilesSelected = (files: File[]) => {
    if (files.length > 0) {
      setImages((prev) => [...prev, ...files]);
    }
  };

  const removeImage = (index: number) => {
    setImages(images.filter((_, i) => i !== index));
  };

  const moveImageUp = (index: number) => {
    if (index === 0) return;
    const newImages = [...images];
    [newImages[index - 1], newImages[index]] = [newImages[index], newImages[index - 1]];
    setImages(newImages);
  };

  const moveImageDown = (index: number) => {
    if (index === images.length - 1) return;
    const newImages = [...images];
    [newImages[index], newImages[index + 1]] = [newImages[index + 1], newImages[index]];
    setImages(newImages);
  };

  const convertToPDF = async () => {
    if (images.length === 0) return;

    setIsProcessing(true);

    try {
      const pdfDoc = await PDFDocument.create();

      for (const imageFile of images) {
        const imageBytes = await imageFile.arrayBuffer();
        let image;

        if (imageFile.type === "image/jpeg" || imageFile.type === "image/jpg") {
          image = await pdfDoc.embedJpg(imageBytes);
        } else if (imageFile.type === "image/png") {
          image = await pdfDoc.embedPng(imageBytes);
        } else {
          continue;
        }

        const page = pdfDoc.addPage([image.width, image.height]);
        page.drawImage(image, {
          x: 0,
          y: 0,
          width: image.width,
          height: image.height,
        });
      }

      const pdfBytes = await pdfDoc.save();
      const blob = new Blob([pdfBytes as BlobPart], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `images-to-pdf-${Date.now()}.pdf`;
      link.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Error converting to PDF:", error);
      alert(copy.errorConvertingImagesToPdf);
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="space-y-8">
      <div className="rounded-[20px] border border-positivus-dark/10 bg-positivus-green/10 p-6">
        <div className="flex items-start gap-4">
          <div className="hidden md:block md:block rounded-full bg-positivus-green/20 p-2">
            <Icon icon="mdi:file-image" className="h-6 w-6 text-positivus-dark" />
          </div>
          <div className="text-sm text-positivus-dark w-full">
            <p className="font-medium text-lg mb-2">{localizedTool.name}</p>
            <p className="leading-relaxed">{localizedTool.description}</p>
          </div>
        </div>
      </div>

      <div className="space-y-4">
         <FileUploader 
            accept=".jpg,.jpeg,.png" 
            multiple
            onFilesSelected={handleFilesSelected}
          />
      </div>

      {images.length > 0 && (
        <div className="space-y-6">
           <div className="flex items-center justify-between">
              <h3 className="text-xl font-bold text-positivus-dark">{copy.imagesToConvert} ({images.length})</h3>
               <button
                  onClick={() => setImages([])}
                  className="text-gray-400 hover:text-red-500 text-sm font-medium flex items-center gap-1 transition-colors"
                >
                  <Icon icon={uiIcons.trash} className="h-4 w-4" />
                  {copy.clearAll}
                </button>
           </div>
           
          <div className="grid gap-4 sm:grid-cols-2">
            {images.map((image, index) => (
              <div
                key={index}
                className="group relative flex flex-col gap-3 rounded-[20px] border-2 border-positivus-dark/10 bg-white p-4 hover:border-positivus-dark transition-all"
              >
                <div className="flex items-center justify-between">
                   <div className="flex items-center gap-3 overflow-hidden">
                       <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-[12px] bg-blue-100 text-blue-600">
                          <Icon icon={uiIcons.image} className="h-5 w-5" />
                       </div>
                       <div className="min-w-0">
                          <p className="font-semibold text-positivus-dark truncate">{image.name}</p>
                          <p className="text-xs text-gray-500">{(image.size / 1024).toFixed(1)} KB</p>
                       </div>
                   </div>
                   <button
                    onClick={() => removeImage(index)}
                    className="rounded-full p-2 text-gray-400 hover:bg-red-50 hover:text-red-500 transition-all opacity-0 group-hover:opacity-100"
                  >
                    <Icon icon={uiIcons.close} className="h-5 w-5" />
                  </button>
                </div>

                <div className="flex items-center gap-2 mt-auto">
                  <button
                    onClick={() => moveImageUp(index)}
                    disabled={index === 0}
                    className="flex-1 flex items-center justify-center gap-2 rounded-[10px] bg-gray-100 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <Icon icon="mdi:arrow-up" className="h-4 w-4" />
                    {copy.moveUp}
                  </button>
                  <button
                    onClick={() => moveImageDown(index)}
                    disabled={index === images.length - 1}
                    className="flex-1 flex items-center justify-center gap-2 rounded-[10px] bg-gray-100 py-2 text-sm font-medium text-gray-600 hover:bg-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                  >
                    <Icon icon="mdi:arrow-down" className="h-4 w-4" />
                    {copy.moveDown}
                  </button>
                </div>
              </div>
            ))}
          </div>

          <button
            onClick={convertToPDF}
            disabled={isProcessing}
            className="w-full rounded-[20px] bg-positivus-dark px-6 py-4 text-xl font-medium text-white hover:bg-positivus-green hover:text-positivus-dark transition-all shadow-[0_5px_0_0_#191A23] hover:shadow-none hover:translate-y-[2px] disabled:opacity-50 disabled:cursor-not-allowed disabled:shadow-none disabled:hover:translate-y-0 disabled:hover:bg-positivus-dark disabled:hover:text-white"
          >
             {isProcessing ? (
                <div className="flex items-center justify-center gap-3">
                    <Icon icon="mdi:loading" className="h-6 w-6 animate-spin" />
                    <span>{copy.convertingToPdf}</span>
                </div>
            ) : copy.convertToPdf}
          </button>
        </div>
      )}
    </div>
  );
}
